/* =====================================================================
   Header Premium — interactions
   - Mega menu / dropdown open on hover & focus (desktop)
   - Click-tap support for touch devices
   - Search overlay (open/close, focus trap, Esc, hide suggestions while typing)
   - Mobile drawer (open/close, expandable submenus)
   - Sticky header subtle shadow on scroll
   ===================================================================== */
(function () {
  'use strict';

  const root = document.querySelector('.header-premium');
  if (!root) return;

  const body = document.body;
  const isTouch = window.matchMedia('(hover: none)').matches;

  /* ---------- Sticky shadow ---------- */
  if (root.classList.contains('header-premium--sticky')) {
    let lastScrolled = false;
    const onScroll = () => {
      const scrolled = window.scrollY > 8;
      if (scrolled !== lastScrolled) {
        root.classList.toggle('is-scrolled', scrolled);
        lastScrolled = scrolled;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Desktop dropdowns / mega ----------
     STRICT HOVER MODEL — opens only via the trigger <a> itself.
     Stays open while cursor is over EITHER:
       (a) that item's trigger link, or
       (b) that item's panel (.header-premium__catalog or __dropdown).
     Closes the moment the cursor is over neither (with an 80 ms grace
     buffer so the 6 px gap between trigger and panel is forgiving).
     Each item owns its own state. No shared parents, no header-wide
     hover region, no CSS :hover chains.
  ---------------------------------------------------- */
  const navItems = root.querySelectorAll('[data-mega], [data-dropdown]');
  let openItem = null;
  let hideTimer = null;

  const HOVER_CLOSE_DELAY = 80;

  const closeOpen = () => {
    clearTimeout(hideTimer);
    if (openItem) {
      openItem.classList.remove('is-open');
      const link = openItem.querySelector('.header-premium__nav-link');
      if (link) link.setAttribute('aria-expanded', 'false');
      openItem = null;
    }
  };

  const openItemEl = (item) => {
    if (openItem === item) {
      clearTimeout(hideTimer);
      return;
    }
    closeOpen();
    item.classList.add('is-open');
    const link = item.querySelector('.header-premium__nav-link');
    if (link) link.setAttribute('aria-expanded', 'true');
    openItem = item;
  };

  // Returns true if (clientX, clientY) is over the trigger, the panel, OR the
  // narrow corridor directly between them. The corridor is constrained to the
  // horizontal overlap of trigger and panel — so neighboring nav items (which
  // are now widely spaced) cannot accidentally fall inside the open item's
  // hover region.
  const isPointerInsideItem = (item, x, y) => {
    if (!item) return false;
    const trigger = item.querySelector('.header-premium__nav-link');
    if (trigger) {
      const t = trigger.getBoundingClientRect();
      if (x >= t.left && x <= t.right && y >= t.top && y <= t.bottom) return true;
    }
    if (item.classList.contains('is-open')) {
      const panel = item.querySelector('.header-premium__catalog, .header-premium__dropdown');
      if (panel) {
        const p = panel.getBoundingClientRect();
        if (x >= p.left && x <= p.right && y >= p.top && y <= p.bottom) return true;
        // Corridor between trigger bottom and panel top — width is the
        // INTERSECTION of trigger and panel horizontal ranges so it never
        // extends sideways into a neighboring item's territory.
        if (trigger) {
          const t = trigger.getBoundingClientRect();
          const corridorLeft = Math.max(t.left, p.left);
          const corridorRight = Math.min(t.right, p.right);
          if (
            corridorRight > corridorLeft &&
            x >= corridorLeft &&
            x <= corridorRight &&
            y >= t.bottom &&
            y <= p.top
          ) return true;
        }
      }
    }
    return false;
  };

  navItems.forEach((item) => {
    const link = item.querySelector('.header-premium__nav-link');
    if (!link) return;

    if (!isTouch) {
      // OPEN trigger is the <a> itself — never the <li>, never a parent.
      link.addEventListener('mouseenter', () => {
        clearTimeout(hideTimer);
        openItemEl(item);
      });

      // Cancel close if focus comes back inside this item's subtree only.
      item.addEventListener('focusin', () => {
        clearTimeout(hideTimer);
        openItemEl(item);
      });
    }

    // Keyboard / touch toggle
    link.addEventListener('click', (e) => {
      if (isTouch && !item.classList.contains('is-open')) {
        e.preventDefault();
        openItemEl(item);
      }
    });
  });

  // Single document-level pointer arbiter. Decides ONLY whether the currently
  // open panel should stay open or close. Never opens anything — opening is
  // strictly the responsibility of the trigger <a> mouseenter handler above.
  if (!isTouch) {
    document.addEventListener('mousemove', (e) => {
      if (!openItem) return;
      if (isPointerInsideItem(openItem, e.clientX, e.clientY)) {
        clearTimeout(hideTimer);
        return;
      }
      // Cursor is outside the open item's trigger + panel. Start close timer.
      if (!hideTimer) {
        hideTimer = setTimeout(() => {
          hideTimer = null;
          closeOpen();
        }, HOVER_CLOSE_DELAY);
      }
    }, { passive: true });
  }

  // Close on scroll — any sticky-open panel feels broken once page moves.
  window.addEventListener('scroll', closeOpen, { passive: true });
  // Close when the tab/window loses focus.
  window.addEventListener('blur', closeOpen);

  /* ---------- Catalog preview swap ---------- */
  root.querySelectorAll('[data-mega="catalog"]').forEach((catalogItem) => {
    const triggers = catalogItem.querySelectorAll('[data-catalog-trigger]');
    const previews = catalogItem.querySelectorAll('[data-catalog-preview]');
    if (!triggers.length || !previews.length) return;

    const activate = (key) => {
      if (!key) return;
      triggers.forEach((t) => {
        const itemEl = t.closest('[data-catalog-item]');
        const matches = itemEl && itemEl.getAttribute('data-key') === key;
        t.classList.toggle('is-active', !!matches);
      });
      previews.forEach((p) => {
        const matches = p.getAttribute('data-key') === key;
        p.classList.toggle('is-active', matches);
        p.setAttribute('aria-hidden', matches ? 'false' : 'true');
      });
    };

    triggers.forEach((trigger) => {
      const itemEl = trigger.closest('[data-catalog-item]');
      if (!itemEl) return;
      const key = itemEl.getAttribute('data-key');
      trigger.addEventListener('mouseenter', () => activate(key));
      trigger.addEventListener('focus', () => activate(key));
    });
  });

  // Close on outside click / Escape
  document.addEventListener('click', (e) => {
    if (openItem && !openItem.contains(e.target)) closeOpen();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeOpen();
  });

  /* ---------- Search overlay ----------
     Owned entirely by <premium-search> custom element (search-premium.js).
     Open/close triggers via [data-action="open-search"] / [data-action="close-search"]
     are bound by that element. Nothing to do here. */

  /* ---------- Mobile drawer ---------- */
  const drawer = document.getElementById('HeaderPremiumDrawer');

  const openDrawer = () => {
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    body.classList.add('hp-no-scroll');
  };

  const closeDrawer = () => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    body.classList.remove('hp-no-scroll');
  };

  document.querySelectorAll('[data-action="open-mobile"]').forEach((b) =>
    b.addEventListener('click', (e) => {
      e.preventDefault();
      openDrawer();
    })
  );
  document.querySelectorAll('[data-action="close-mobile"]').forEach((b) =>
    b.addEventListener('click', (e) => {
      e.preventDefault();
      closeDrawer();
    })
  );

  // Expandable submenus inside drawer (smooth accordion via max-height)
  document.querySelectorAll('[data-action="toggle-sub"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      const wrap = btn.nextElementSibling;
      if (!wrap) return;

      if (expanded) {
        // Closing
        btn.setAttribute('aria-expanded', 'false');
        // Set to current scroll height first, then to 0 next frame for smooth collapse
        wrap.style.maxHeight = wrap.scrollHeight + 'px';
        requestAnimationFrame(() => {
          wrap.style.maxHeight = '0px';
        });
      } else {
        // Opening — close any sibling open accordions in the same list
        const parentList = btn.closest('ul');
        if (parentList) {
          parentList.querySelectorAll('[data-action="toggle-sub"][aria-expanded="true"]').forEach((other) => {
            if (other === btn) return;
            other.setAttribute('aria-expanded', 'false');
            const otherWrap = other.nextElementSibling;
            if (otherWrap) {
              otherWrap.style.maxHeight = otherWrap.scrollHeight + 'px';
              requestAnimationFrame(() => { otherWrap.style.maxHeight = '0px'; });
            }
          });
        }
        btn.setAttribute('aria-expanded', 'true');
        wrap.style.maxHeight = wrap.scrollHeight + 'px';
        // After transition completes, clear inline so resize works
        const onEnd = () => {
          if (btn.getAttribute('aria-expanded') === 'true') {
            wrap.style.maxHeight = 'none';
          }
          wrap.removeEventListener('transitionend', onEnd);
        };
        wrap.addEventListener('transitionend', onEnd);
      }
    });
  });

  // Global Escape closes overlays
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (drawer && drawer.classList.contains('is-open')) closeDrawer();
  });
})();
